import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { AutocompleteOption } from '../../inputs/autocomplete-select/autocomplete-select.component';

@Component({
  selector: 'app-journal-entry-lines',
  standalone: false,
  templateUrl: './journal-entry-lines.html',
  styleUrl: './journal-entry-lines.scss',
})
export class JournalEntryLines {
  @Input() record: Record<string, unknown> | null = null;
  @Input() items: Array<Record<string, unknown>> = [];
  @Input() preview = false;
  @Input() loading = false;
  @Input() error = '';
  @Input() linkId: string | null = null;
  @Input() emptyMessage = 'Post or save the document, then reopen this tab to inspect its balanced journal entry.';
  @Input() provisionalKind: 'sale' | 'purchase' | 'customer_credit' | 'vendor_credit' | 'inbound_payment' | 'outbound_payment' | 'inventory' | 'asset' | 'deferred_expense' | 'deferred_revenue' | 'transfer' | null = null;
  @Input() provisionalUntaxed = 0;
  @Input() provisionalTax = 0;
  @Input() provisionalTotal = 0;
  @Input() provisionalSourceName = 'Source cash or bank';
  @Input() provisionalDestinationName = 'Destination cash or bank';

  get displayRecord(): Record<string, unknown> | null {
    if (this.record) return this.record;
    if (!this.provisionalKind) return null;
    const items = this.displayItems;
    const debit = items.reduce((sum, item) => sum + Number(item['debit'] ?? 0), 0);
    const credit = items.reduce((sum, item) => sum + Number(item['credit'] ?? 0), 0);
    return { name: 'Live estimate from this unsaved form', state: 'draft',
      total_debit: this.provisionalAmount(debit), total_credit: this.provisionalAmount(credit) };
  }

  get displayItems(): Array<Record<string, unknown>> {
    if (this.record) return this.items;
    const total = this.provisionalAmount(this.provisionalTotal);
    const untaxed = this.provisionalAmount(this.provisionalUntaxed);
    const tax = this.provisionalAmount(this.provisionalTax);
    if (!this.provisionalKind || (total === 0 && untaxed === 0 && tax === 0)) return [];
    const line = (name: string, debit: number, credit: number): Record<string, unknown> =>
      ({ account_name: name, label: 'Estimated account; confirmed when saved', debit, credit });
    const signed = (name: string, amount: number, debitWhenPositive: boolean): Record<string, unknown> =>
      line(name, this.provisionalAmount(debitWhenPositive ? Math.max(amount, 0) : Math.max(-amount, 0)),
        this.provisionalAmount(debitWhenPositive ? Math.max(-amount, 0) : Math.max(amount, 0)));
    if (this.provisionalKind === 'inbound_payment') return [signed('Cash or bank selected for payment', total, true), signed('Customer receivable', total, false)];
    if (this.provisionalKind === 'outbound_payment') return [signed('Vendor payable', total, true), signed('Cash or bank selected for payment', total, false)];
    if (this.provisionalKind === 'transfer') return [signed(this.provisionalDestinationName, total, true), signed(this.provisionalSourceName, total, false)];
    if (this.provisionalKind === 'inventory') return [];
    if (this.provisionalKind === 'asset') return [signed('Fixed asset', total, true), signed('Vendor payable or acquisition clearing', total, false)];
    if (this.provisionalKind === 'deferred_expense') return [signed('Expense recognized over schedule', total, true), signed('Deferred expense released over schedule', total, false)];
    if (this.provisionalKind === 'deferred_revenue') return [signed('Deferred revenue released over schedule', total, true), signed('Revenue recognized over schedule', total, false)];
    const purchase = this.provisionalKind === 'purchase';
    const customerCredit = this.provisionalKind === 'customer_credit';
    const vendorCredit = this.provisionalKind === 'vendor_credit';
    const rows = purchase
      ? [signed('Inventory or expense', untaxed, true), signed('Vendor payable', total, false)]
      : customerCredit
        ? [signed('Sales returns', untaxed, true), signed('Customer credit', total, false)]
        : vendorCredit
          ? [signed('Vendor payable', total, true), signed('Inventory or expense reversal', untaxed, false)]
          : [signed('Customer receivable', total, true), signed('Sales revenue', untaxed, false)];
    const baseDebit = purchase || customerCredit;
    if (tax) rows.push(signed(purchase ? 'Input tax' : customerCredit ? 'Output tax reversal' : vendorCredit ? 'Input tax reversal' : 'Output tax', tax, baseDebit));
    const rounding = this.provisionalAmount(total - untaxed - tax);
    if (rounding) rows.push(signed('Rounding adjustment', rounding, baseDebit));
    return rows;
  }

  get displayPreview(): boolean { return this.preview || !this.record; }
  get isProvisional(): boolean { return !this.record && !!this.provisionalKind; }

  // Only Finance's manual draft editor supplies a form. Other owners render
  // their canonical preview/posted rows without granting this component write access.
  @Input() entryForm: FormGroup | null = null;
  @Input() readOnly = false;
  @Input() submitted = false;
  @Input() accountOptions: AutocompleteOption[] = [];
  @Input() partnerOptions: AutocompleteOption[] = [];
  @Input() currencyOptions: Array<{ value: string; label: string }> = [];
  @Input() searchAccounts: (query: string) => Promise<AutocompleteOption[]> = async () => [];
  @Input() searchPartners: (query: string) => Promise<AutocompleteOption[]> = async () => [];
  @Input() accountCode: (line: FormGroup) => string | null = () => null;
  @Output() addLine = new EventEmitter<void>();
  @Output() removeLine = new EventEmitter<number>();

  get lines(): FormArray<FormGroup> | null {
    return this.entryForm?.get('lines') as FormArray<FormGroup> | null;
  }

  get debitTotal(): number {
    return this.lines?.controls.reduce((sum, line) => sum + this.amount(line.get('debit')?.value), 0) ?? 0;
  }

  get creditTotal(): number {
    return this.lines?.controls.reduce((sum, line) => sum + this.amount(line.get('credit')?.value), 0) ?? 0;
  }

  get difference(): number { return this.debitTotal - this.creditTotal; }
  get balanced(): boolean { return this.debitTotal > 0 && Math.abs(this.difference) < 0.0001; }

  lineAmount(line: FormGroup): number {
    return Math.max(this.amount(line.get('debit')?.value), this.amount(line.get('credit')?.value));
  }

  lineSideInvalid(line: FormGroup): boolean {
    const debit = this.amount(line.get('debit')?.value);
    const credit = this.amount(line.get('credit')?.value);
    return debit < 0 || credit < 0 || (debit > 0 && credit > 0) || (debit === 0 && credit === 0);
  }

  text(value: unknown): string { return value == null ? '' : String(value); }
  trackIndex(index: number): number { return index; }
  money(value: unknown): string {
    const parsed = Number(value ?? 0);
    return (Number.isFinite(parsed) ? parsed : 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  private amount(value: unknown): number {
    const numberValue = Array.isArray(value) ? value[0] : value;
    const parsed = Number(numberValue ?? 0);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private provisionalAmount(value: number): number {
    return Math.round((Number.isFinite(Number(value)) ? Number(value) : 0) * 100) / 100;
  }

}
